import React from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { C, FONT_NUM, PAGE_BUTTON_SX } from './tokens';

// Title row + page-level action buttons (export / refresh).
const BreakoutPageHeader = ({
  t,
  scoreDate,
  candidatesCount,
  exportingImage,
  loading,
  onExport,
  onRefresh,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: '1 1 320px' }}>
      <Box sx={{
        width: { xs: 40, sm: 44 },
        height: { xs: 40, sm: 44 },
        borderRadius: '12px',
        flexShrink: 0,
        background: `linear-gradient(135deg, ${C.accent} 0%, #1d4ed8 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 3px 10px ${alpha(C.accent, 0.22)}`,
      }}>
        <RocketLaunchIcon sx={{ fontSize: { xs: 20, sm: 22 }, color: '#fff' }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{
            fontSize: { xs: '1.05rem', sm: '1.2rem' },
            fontWeight: 700,
            color: C.ink,
            letterSpacing: '-0.01em',
            lineHeight: 1.15,
          }}>
            {t('breakoutCandidates.pageTitle')}
          </Typography>
          {scoreDate ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.accent, boxShadow: `0 0 6px ${alpha(C.accent, 0.7)}` }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.inkMid, fontFamily: FONT_NUM }}>
                {scoreDate}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.75rem' }, color: C.mutedSoft, mt: 0.15, lineHeight: 1.35 }}>
          {t('breakoutCandidates.pageSubtitle')}
        </Typography>
      </Box>
    </Box>

    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        width: { xs: '100%', sm: 'auto' },
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
      }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={exportingImage ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
        onClick={onExport}
        disabled={exportingImage || candidatesCount === 0}
        sx={PAGE_BUTTON_SX}
      >
        {exportingImage
          ? t('breakoutCandidates.table.exportingImage')
          : t('breakoutCandidates.table.exportImage')}
      </Button>
      <Button
        variant="outlined"
        size="small"
        startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
        onClick={onRefresh}
        disabled={loading}
        sx={PAGE_BUTTON_SX}
      >
        {t('common.refresh')}
      </Button>
    </Stack>
  </Box>
);

BreakoutPageHeader.propTypes = {
  t: PropTypes.func.isRequired,
  scoreDate: PropTypes.string,
  candidatesCount: PropTypes.number.isRequired,
  exportingImage: PropTypes.bool,
  loading: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
};

BreakoutPageHeader.defaultProps = {
  scoreDate: '',
  exportingImage: false,
  loading: false,
};

export default BreakoutPageHeader;
